import { useEffect } from 'react';

import { RunningExperiments, useExperiments } from 'state/experiments';

const TREATMENT_VARIANT = 'treatment';

/**
 * Reads the deeplinks-flow-feature experiment WITHOUT logging exposure.
 * Safe default: Amplitude slow/fail → `false` → control (legacy full locator /
 * toast UX — no info modal, no minimal locator).
 *
 * Prefer {@link useDeeplinksFlowExperiment} at navigation branch points so a
 * still-loading Amplitude fetch cannot fire the control store-locator redirect
 * before treatment is known.
 */
export const useDeeplinksFlowEnabled = () => {
  const { variant } = useExperiments();

  return variant(RunningExperiments.DeeplinksFlowFeature, TREATMENT_VARIANT, {
    skipExposureLogging: true,
  });
};

/**
 * Deeplinks-flow assignment plus Amplitude readiness.
 * While `isLoading`, do not navigate to the control store locator — show a
 * loader so treatment users never flash the locator over the PDP/offer modal.
 */
export const useDeeplinksFlowExperiment = () => {
  const { isLoading, variant } = useExperiments();

  return {
    isLoading,
    isEnabled: variant(RunningExperiments.DeeplinksFlowFeature, TREATMENT_VARIANT, {
      skipExposureLogging: true,
    }),
  };
};

/**
 * Logs Amplitude exposure for deeplinks-flow-feature.
 *
 * Pass `shouldExpose = true` when the user hits a deeplink-flow-eligible surface:
 * no-store menu/offer deeplink, unavailable offer detail settle, or unavailable
 * menu PDP (store menu ready). Must be true for BOTH control and treatment so
 * the whole eligible population is recorded. `logExposure` dedupes.
 */
export const useExposeDeeplinksFlowExperiment = (shouldExpose: boolean) => {
  const { logExposure } = useExperiments();

  useEffect(() => {
    if (!shouldExpose) {
      return;
    }

    logExposure(RunningExperiments.DeeplinksFlowFeature);
  }, [shouldExpose, logExposure]);
};
